import { z } from 'zod';
import { SubjectSchema } from '../common/schemas';
import { LegacyExamExamIdSchema, LegacyExamLabelSchema } from '../legacyExams/schemas';
import { LegacyExamTypeTypeSchema } from '../legacyExamTypes/schemas';
import { DBStudentStudentIdSchema } from '../students/schemas';
import { LegacyTopicTopicIdSchema } from '../legacyTopics/schemas';
import { DEFAULT_LEGACY_TOPIC_ANALYTICS_MARKS_TALLY } from './constants';

/**
 * Shared & Helper Schemas
 */

const DBStudentTopicAnalyticsMarksTallySchema = z.object({
  attemptedMarks: z.number().int().min(0),
  inaccuracyMarks: z.number().int().min(0),
  missedMarks: z.number().int().min(0),
  sillyMarks: z.number().int().min(0),
});

/**
 * DB Schemas
 */

export const DBLegacyStudentTopicAnalyticsStudentSchema = z.lazy(() => DBStudentStudentIdSchema);
export const DBLegacyStudentTopicAnalyticsSubjectTopicIdentifierSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 3 &&
    z.literal('TOPIC').safeParse(parts[0]).success &&
    z.lazy(() => SubjectSchema).safeParse(parts[1]).success &&
    z.lazy(() => LegacyTopicTopicIdSchema).safeParse(parts[2]).success
  );
});
export const DBLegacyStudentTopicAnalyticsAnalyticsSchema = DBStudentTopicAnalyticsMarksTallySchema;
export const DBLegacyStudentTopicAnalyticsCalculationAnalyticsSchema =
  DBStudentTopicAnalyticsMarksTallySchema;
export const DBLegacyStudentTopicAnalyticsExtendedResponseAnalyticsSchema =
  DBStudentTopicAnalyticsMarksTallySchema;
export const DBLegacyStudentTopicAnalyticsGraphingAnalyticsSchema =
  DBStudentTopicAnalyticsMarksTallySchema;
export const DBLegacyStudentTopicAnalyticsMultipleChoiceAnalyticsSchema =
  DBStudentTopicAnalyticsMarksTallySchema;
export const DBLegacyStudentTopicAnalyticsProofAnalyticsSchema =
  DBStudentTopicAnalyticsMarksTallySchema;
export const DBLegacyStudentTopicAnalyticsShortAnswerAnalyticsSchema =
  DBStudentTopicAnalyticsMarksTallySchema;
export const DBLegacyStudentTopicAnalyticsTopicExamsSchema = z.record(
  z.lazy(() => LegacyExamExamIdSchema),
  z.lazy(() =>
    z.object({
      label: z.lazy(() => LegacyExamLabelSchema),
      type: z.lazy(() => LegacyExamTypeTypeSchema),
      value: z.lazy(() => LegacyExamExamIdSchema),
    })
  )
);

export const DBLegacyStudentTopicAnalyticsSchema = z.object({
  student: DBLegacyStudentTopicAnalyticsStudentSchema,
  subjectTopicIdentifier: DBLegacyStudentTopicAnalyticsSubjectTopicIdentifierSchema,
  analytics: DBLegacyStudentTopicAnalyticsAnalyticsSchema,
  calculationAnalytics: DBLegacyStudentTopicAnalyticsCalculationAnalyticsSchema.optional(),
  extendedResponseAnalytics:
    DBLegacyStudentTopicAnalyticsExtendedResponseAnalyticsSchema.optional(),
  graphingAnalytics: DBLegacyStudentTopicAnalyticsGraphingAnalyticsSchema.optional(),
  multipleChoiceAnalytics: DBLegacyStudentTopicAnalyticsMultipleChoiceAnalyticsSchema.optional(),
  proofAnalytics: DBLegacyStudentTopicAnalyticsProofAnalyticsSchema.optional(),
  shortAnswerAnalytics: DBLegacyStudentTopicAnalyticsShortAnswerAnalyticsSchema.optional(),
  // Optional to allow removal of topic exams
  topicExams: DBLegacyStudentTopicAnalyticsTopicExamsSchema.optional(),
});

/**
 * Client Schemas
 */

const StudentTopicAnalyticsMarksTallySchema = z.object({
  attemptedMarks: z.number().int().min(0).default(0),
  inaccuracyMarks: z.number().int().min(0).default(0),
  missedMarks: z.number().int().min(0).default(0),
  sillyMarks: z.number().int().min(0).default(0),
});

export const LegacyStudentTopicAnalyticsStudentIdSchema =
  DBLegacyStudentTopicAnalyticsStudentSchema;
export const LegacyStudentTopicAnalyticsSubjectSchema = z.lazy(() => SubjectSchema);
export const LegacyStudentTopicAnalyticsTopicIdSchema = z.lazy(() => LegacyTopicTopicIdSchema);
export const LegacyStudentTopicAnalyticsAnalyticsSchema = StudentTopicAnalyticsMarksTallySchema;
export const LegacyStudentTopicAnalyticsCalculationAnalyticsSchema =
  StudentTopicAnalyticsMarksTallySchema;
export const LegacyStudentTopicAnalyticsExtendedResponseAnalyticsSchema =
  StudentTopicAnalyticsMarksTallySchema;
export const LegacyStudentTopicAnalyticsGraphingAnalyticsSchema =
  StudentTopicAnalyticsMarksTallySchema;
export const LegacyStudentTopicAnalyticsMultipleChoiceAnalyticsSchema =
  StudentTopicAnalyticsMarksTallySchema;
export const LegacyStudentTopicAnalyticsProofAnalyticsSchema =
  StudentTopicAnalyticsMarksTallySchema;
export const LegacyStudentTopicAnalyticsShortAnswerAnalyticsSchema =
  StudentTopicAnalyticsMarksTallySchema;
export const LegacyStudentTopicAnalyticsTopicExamsSchema = z.lazy(() =>
  z.array(
    z.object({
      label: z.lazy(() => LegacyExamLabelSchema),
      type: z.lazy(() => LegacyExamTypeTypeSchema),
      examId: z.lazy(() => LegacyExamExamIdSchema),
    })
  )
);

export const LegacyStudentTopicAnalyticsSchema = z.object({
  studentId: LegacyStudentTopicAnalyticsStudentIdSchema,
  subject: LegacyStudentTopicAnalyticsSubjectSchema,
  topicId: LegacyStudentTopicAnalyticsTopicIdSchema,
  analytics: LegacyStudentTopicAnalyticsAnalyticsSchema.default(
    DEFAULT_LEGACY_TOPIC_ANALYTICS_MARKS_TALLY
  ),
  calculationAnalytics: LegacyStudentTopicAnalyticsCalculationAnalyticsSchema.optional(),
  extendedResponseAnalytics: LegacyStudentTopicAnalyticsExtendedResponseAnalyticsSchema.optional(),
  graphingAnalytics: LegacyStudentTopicAnalyticsGraphingAnalyticsSchema.optional(),
  multipleChoiceAnalytics: LegacyStudentTopicAnalyticsMultipleChoiceAnalyticsSchema.optional(),
  proofAnalytics: LegacyStudentTopicAnalyticsProofAnalyticsSchema.optional(),
  shortAnswerAnalytics: LegacyStudentTopicAnalyticsShortAnswerAnalyticsSchema.optional(),
  topicExams: LegacyStudentTopicAnalyticsTopicExamsSchema.default([]),
});
